import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { useQuery } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { isNumber, isString } from 'lodash';
import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useMap } from 'react-map-gl/mapbox';
import { Link } from 'react-router';
import { locationsSelectedFeatureIdAtom } from 'src/atoms/filters/locations';
import { getSingleLocationOptions } from 'src/clients/locations/Device Locations API/@tanstack/react-query.gen';
import { CslCmpDeviceLocationsApiContractsGeoJsonFeatureCollectionV1 } from 'src/clients/locations/Device Locations API/types.gen';
import { Button } from 'src/common/components/Buttons/Button';
import { DetailsSkeleton } from 'src/common/components/DetailsSkeleton';
import { ScrollArea } from 'src/common/components/ScrollArea/ScrollArea';
import { SectionGridItem } from 'src/common/components/SectionGrid/SectionGridItem';
import { SectionGridRow } from 'src/common/components/SectionGrid/SectionGridRow';
import { mapSimDTO } from 'src/common/components/Sim/Inventory/utils/simListResponseParsers';
import { Routes } from 'src/Routes.constants';
import { fetchSimInventoryEntryByImsi } from 'src/simInventory/SimInventoryApi/simInventoryApi';
import { simDetailsQueries } from 'src/simInventory/SimInventoryDetails/queries/simDetailsQueries';

const productTypeTableMap: Record<string, { pathname: Routes }> = {
  SIM: { pathname: Routes.productsSim },
  rSIM: { pathname: Routes.productsRsim },
};

type LocationsMapSelectedContentProps = {
  data: ReturnType<typeof mapSimDTO>[];
  locationData: CslCmpDeviceLocationsApiContractsGeoJsonFeatureCollectionV1;
};
const LocationsMapSelectedContent: FC<LocationsMapSelectedContentProps> = (props) => {
  const { data, locationData } = props;

  const simLocationFeature = locationData.features?.[0];
  const locationProperties = simLocationFeature?.properties;

  const { t } = useTranslation();

  return (
    <Stack
      useFlexGap
      spacing={10}
      height="100%"
      overflow="hidden"
      data-testid={`locations.map.deviceDetails.${data[0].imsi}`}
    >
      <ScrollArea>
        <SectionGridRow
          data-testid={`locationDetails.fields.${t('simInventory.simProfile')}`}
          title={t('simInventory.simProfile')}
          sx={{ borderTop: 0 }}
        >
          <SectionGridItem label={t('simInventory.sessionStatus')} value={data[0].sessionStatus} />
          <SectionGridItem label={t('simInventory.productType')} value={data[0].productType} />
          <SectionGridItem
            label={t('simInventory.sessionStatusLastUpdated_flat')}
            value={data[0].sessionStatusLastUpdated}
          />
          <SectionGridItem label={t('simInventory.productCode')} value={data[0].productCode} />
          <SectionGridItem
            label={t('simInventory.subscriptionStatus')}
            value={data[0].subscriptionStatus ? data[0].subscriptionStatus.toString() : undefined}
          />
          <SectionGridItem label={t('simInventory.simType')} value={data[0].simType} />
        </SectionGridRow>
        {locationProperties ? (
          <SectionGridRow
            data-testid={`locationDetails.fields.${t('simInventory.lastKnownLocation')}`}
            title={t('simInventory.lastKnownLocation')}
            sx={{ borderTop: 0 }}
          >
            <SectionGridItem
              label="Last Known Status"
              value={
                isString(locationProperties.currentStatus)
                  ? locationProperties.currentStatus
                  : undefined
              }
            />
            <SectionGridItem
              label={t('simInventory.accuracy')}
              value={
                isNumber(locationProperties.accuracy)
                  ? locationProperties.accuracy.toString()
                  : undefined
              }
            />
            <SectionGridItem
              label={t('simInventory.latitude')}
              value={
                isNumber(simLocationFeature?.geometry?.coordinates?.[1])
                  ? simLocationFeature.geometry.coordinates?.[1].toLocaleString()
                  : undefined
              }
            />
            <SectionGridItem
              label={t('simInventory.longitude')}
              value={
                isNumber(simLocationFeature?.geometry?.coordinates?.[0])
                  ? simLocationFeature.geometry.coordinates[0].toLocaleString()
                  : undefined
              }
            />
            <SectionGridItem
              label={t('simInventory.lastUpdated')}
              value={
                isString(locationProperties.eventTimestamp)
                  ? locationProperties.eventTimestamp
                  : undefined
              }
            />
          </SectionGridRow>
        ) : null}
      </ScrollArea>
      <Link
        data-testid={`locations.map.deviceDetails.${'SIM'}.${data[0].iccid}.viewLink`}
        to={{
          pathname: `${productTypeTableMap['SIM'].pathname}/${data[0].iccid}`,
        }}
      >
        <Button
          data-testid={`locations.map.deviceDetails.${'SIM'}.${data[0].iccid}.viewButton`}
          variant="primary"
        >
          {t('products.sim.viewDetails')}
        </Button>
      </Link>
    </Stack>
  );
};

type LocationsMapSelectedOverlayProps = {
  selectedImsi: string;
};
export const LocationsMapSelectedOverlay: FC<LocationsMapSelectedOverlayProps> = (props) => {
  const { selectedImsi } = props;
  const setSelectedId = useSetAtom(locationsSelectedFeatureIdAtom);

  const { t } = useTranslation();
  const { 'device-locations': map } = useMap();

  const { data, status } = useQuery({
    queryKey: simDetailsQueries.detailByImsi(selectedImsi),
    queryFn: async ({ signal }) => {
      return fetchSimInventoryEntryByImsi(selectedImsi, signal);
    },
  });

  const simLocationDetailOptions = getSingleLocationOptions({
    query: {
      DeviceId: selectedImsi,
    },
  });

  const { data: simLocationDetailData, status: simLocationDetailStatus } = useQuery({
    ...simLocationDetailOptions,
  });

  return (
    <Fragment>
      <CardHeader
        data-testid="locations.map.deviceDetails.header"
        action={
          <IconButton
            aria-label="Close"
            onClick={() => {
              setSelectedId(null);
              map?.flyTo({ padding: { right: 0 } });
            }}
          >
            <CloseIcon />
          </IconButton>
        }
        title={
          <span data-testid="locations.map.deviceDetails.header.title">
            {t('locations.map.selectedDevice')}
          </span>
        }
        subheader={
          <span data-testid="locations.map.deviceDetails.header.subtitle">{selectedImsi}</span>
        }
      />
      <CardContent sx={{ height: '100%', flexGrow: 1, flexShrink: 1, overflow: 'hidden' }}>
        {status === 'pending' ? <DetailsSkeleton numberOfRepetitions={2} /> : null}
        {status === 'error' ? <Box>{t('common.somethingWrong')}</Box> : null}
        {status === 'success' && simLocationDetailStatus === 'success' && data.items.length > 0 ? (
          <LocationsMapSelectedContent data={data.items} locationData={simLocationDetailData} />
        ) : null}
      </CardContent>
    </Fragment>
  );
};
